import React from 'react';
import I18n from 'i18n-js';

import { gql } from '@reverbdotcom/commons/src/gql';
import { RCModal, RCRadioCardGroup, RCIcon, RCText, RCTag } from '@reverbdotcom/cadence/components';
import { BankIcon, ReverbLogoCircleIcon } from '@reverbdotcom/cadence/icons/react';
import { useMutation } from '@reverbdotcom/commons/src/useMutation';
import {
  Listing,
  CrupdateListingPayoutToWalletMutation,
  CrupdateListingPayoutToWalletMutationVariables,
  core_apimessages_UpdateListingRequest_UpdateType,
} from '@reverbdotcom/commons/src/gql/graphql';

import SellerListingCardContext from './SellerListingCardContext';
import SellerListingsCollectionContext from './SellerListingsCollectionContext';
import { ActionType } from './sellerListingCardReducer';
import { WalletValueProp, BankValueProp, WalletAgreeToTermsCheckBox } from '../wallet_eligible_payout_method_selection';

function boolStringToBool(string: string) {
  const lowerCaseString = string.toLowerCase();
  if (lowerCaseString === 'true') return true;
  if (lowerCaseString === 'false') return false;

  return null;
}

export interface SellerListingCardPaymentSettingsModalContentProps {
  listing: Listing;
  selectedPayoutToWallet: boolean;
  acceptedWalletTerms: boolean;
  acceptedWalletErrorText: string;
  selectionHasNotChanged: boolean;
  setSelectedPayoutToWallet: (boolean: boolean) => void;
  setAcceptedWalletTerms: (boolean: boolean) => void;
  setAcceptedWalletErrorText: (string: string | null) => void;
  setSelectionHasNotChanged: (boolean: boolean) => void;
}

export function SellerListingCardPaymentSettingsModalContent({
  listing,
  selectedPayoutToWallet,
  acceptedWalletTerms,
  acceptedWalletErrorText,
  selectionHasNotChanged,
  setSelectedPayoutToWallet,
  setAcceptedWalletTerms,
  setAcceptedWalletErrorText,
  setSelectionHasNotChanged,
}: SellerListingCardPaymentSettingsModalContentProps) {
  return (
    <RCRadioCardGroup
      id="listing-payment-settings-selection"
      name="listing-payment-settings-selection"
      onChange={(e) => {
        setSelectedPayoutToWallet(boolStringToBool(e.target.value));
        setSelectionHasNotChanged(false);
      }}
      value={selectedPayoutToWallet.toString()}
      iconPlacement="hidden"
      label=""
    >
      <div className="width-100 tablet-width-100">
        <RCRadioCardGroup.Option
          label={
            <div className="d-flex fx-justify-between">
              <div className="d-flex fx-align-center gap-2">
                <div className="text-color-vu-orange-600">
                  <RCIcon icon={<ReverbLogoCircleIcon />} size="8" />
                </div>
                <RCText.Utility size="450" weight="bold" htmlTag="span">
                  {I18n.t('js.dashboard.selling.shopPolicies.directCheckoutProfile.reverbWalletAvailable.payoutMethodSelection.wallet.label')}
                </RCText.Utility>
              </div>
              {listing.payoutToWallet &&
                <div className="d-flex fx-align-center">
                  <RCTag>
                    {I18n.t('discovery.sellForm.pricingSection.walletPayout.optInEditModal.currentSelection')}
                  </RCTag>
                </div>
              }
            </div>
          }
          value="true"
        >
          {(selectedPayoutToWallet || selectionHasNotChanged) &&
            <WalletValueProp />
          }
        </RCRadioCardGroup.Option>
      </div>
      <div className="width-100 tablet-width-100">
        <RCRadioCardGroup.Option
          label={
            <div className="d-flex fx-justify-between">
              <div className="d-flex fx-align-center gap-2">
                <div className="bg-blue bd-radius-full text-color-bw-white padding-1">
                  <RCIcon icon={<BankIcon />} size="6" />
                </div>
                <RCText.Utility size="450" weight="bold" htmlTag="span">
                  {I18n.t('js.dashboard.selling.shopPolicies.directCheckoutProfile.reverbWalletAvailable.payoutMethodSelection.ach.label')}
                </RCText.Utility>
              </div>
              {!listing.payoutToWallet &&
                <div className="d-flex fx-align-center">
                  <RCTag>
                    {I18n.t('discovery.sellForm.pricingSection.walletPayout.optInEditModal.currentSelection')}
                  </RCTag>
                </div>
              }
            </div>
          }
          value="false"
        >
          {(!selectedPayoutToWallet || selectionHasNotChanged) &&
            <BankValueProp />
          }
        </RCRadioCardGroup.Option>
      </div>
      {selectedPayoutToWallet && (
        <div className="wallet-accept-terms mt-2">
          <div className="mb-3">
            <RCText.Default size="350" weight="bold">
              {I18n.t('js.dashboard.selling.shopPolicies.directCheckoutProfile.reverbWalletAvailable.payoutMethodSelection.wallet.disclaimer')}
            </RCText.Default>
          </div>
          <WalletAgreeToTermsCheckBox
            checked={acceptedWalletTerms}
            onChange={() => {
              setAcceptedWalletErrorText('');
              setAcceptedWalletTerms(!acceptedWalletTerms);
            }}
            errorText={acceptedWalletErrorText}
            disabled={!selectedPayoutToWallet}
          />
        </div>

      )}
    </RCRadioCardGroup>
  );
}

interface SellerListingCardPaymentSettingsModalProps {
  isModalOpen: boolean;
  setIsModalOpen: (boolean: boolean) => void;
  listing: Listing;
}

export const COMPONENT_NAME = 'SellerListingCardPaymentSettingsModalProps';

export default function SellerListingCardPaymentSettingsModal({
  isModalOpen,
  setIsModalOpen,
  listing,
}: SellerListingCardPaymentSettingsModalProps) {

  const [selectedPayoutToWallet, setSelectedPayoutToWallet] = React.useState(listing.payoutToWallet);
  const [acceptedWalletTerms, setAcceptedWalletTerms] = React.useState(listing.payoutToWallet);
  const [acceptedWalletErrorText, setAcceptedWalletErrorText] = React.useState('');
  const [selectionHasNotChanged, setSelectionHasNotChanged] = React.useState(true);

  const hasMutationBeenCalled = React.useRef(false);
  const { dispatch: cardDispatch } = React.useContext(SellerListingCardContext);
  const { useLazySellerListingsQuery } = React.useContext(SellerListingsCollectionContext);
  const { refetchSellerListings } = useLazySellerListingsQuery();
  const [updateListingPaymentSettings, { loading: mutationLoading, errors: mutationErrors }] = useMutation<CrupdateListingPayoutToWalletMutation, CrupdateListingPayoutToWalletMutationVariables>(CRUPDATE_LISTING_PAYOUT_TO_WALLET, { fetchPolicy: 'no-cache' } );

  React.useEffect(() => {
    if (!hasMutationBeenCalled.current || mutationLoading) return;
    if (mutationErrors.length) return handleErrors();

    handleSuccess();
  }, [mutationLoading]);

  function handleErrors() {
    setIsModalOpen(false);

    cardDispatch({
      type: ActionType.SHOW_TOAST,
      payload: {
        isSuccess: false,
        message: I18n.t('discovery.listingsManagement.sellerListingCard.paymentSettingsError'),
      },
    });
  }

  function handleSuccess() {
    setIsModalOpen(false);

    cardDispatch({
      type: ActionType.SHOW_TOAST,
      payload: {
        isSuccess: true,
        message: I18n.t(
          'discovery.listingsManagement.sellerListingCard.paymentSettingsSuccess',
        ),
      },
    });

    refetchSellerListings([listing.id]);
  }

  function dispatch(payoutToWallet) {
    hasMutationBeenCalled.current = true;
    updateListingPaymentSettings({
      variables: {
        input: {
          updateType: core_apimessages_UpdateListingRequest_UpdateType.SPECIFIC_FIELDS,
          fields: ['payoutToWallet'],
          listingEntry: {
            id: listing.id,
            payoutToWallet,
          },
        },
      },
    });
  }

  function onSaveButtonClick() {
    if (listing.payoutToWallet !== selectedPayoutToWallet) {
      if (selectedPayoutToWallet && !acceptedWalletTerms) {
        setAcceptedWalletErrorText(I18n.t('js.dashboard.selling.shopPolicies.directCheckoutProfile.reverbWalletAvailable.payoutMethodSelection.wallet.terms.required'));
        return;
      }

      dispatch(selectedPayoutToWallet);
    }
    setIsModalOpen(false);
  }

  const disableSaveButton = !listing.payoutToWallet && selectedPayoutToWallet && !acceptedWalletTerms;

  return (
    <RCModal
      isOpen={isModalOpen}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          setIsModalOpen(false);
        }
      }}
      title={I18n.t('js.dashboard.selling.shopPolicies.directCheckoutProfile.reverbWalletAvailable.selectHowYouWantToGetPaid')}
      actions={{
        primary: {
          buttonText: I18n.t('js.dashboard.selling.shopPolicies.directCheckoutProfile.reverbWalletAvailable.updateSettings'),
          disabled: disableSaveButton,
          onClick: onSaveButtonClick,
        },
        secondary: true,
      }}
    >
      <SellerListingCardPaymentSettingsModalContent
        listing={listing}
        selectedPayoutToWallet={selectedPayoutToWallet}
        acceptedWalletTerms={acceptedWalletTerms}
        acceptedWalletErrorText={acceptedWalletErrorText}
        selectionHasNotChanged={selectionHasNotChanged}
        setSelectedPayoutToWallet={setSelectedPayoutToWallet}
        setAcceptedWalletTerms={setAcceptedWalletTerms}
        setAcceptedWalletErrorText={setAcceptedWalletErrorText}
        setSelectionHasNotChanged={setSelectionHasNotChanged}
      />
    </RCModal>
  );
}

export const CRUPDATE_LISTING_PAYOUT_TO_WALLET = gql(`
  mutation CrupdateListingPayoutToWallet($input: Input_core_apimessages_UpdateListingRequest) {
    crupdateListing(input: $input) {
      _id
    }
  }
`);
